<template>
  <div class="create-api-token-wrapper">
    <div class="content-area">
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <v-btn
              icon
              variant="text"
              size="medium"
              class="back-btn"
              @click="goBack">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              Create API Token
            </h1>
          </div>
        </div>
      </div>

      <div class="main-content">
        <div class="left-panel" v-if="!createdToken">
          <div class="form-content">
            <div class="form-group">
              <label class="form-label required">
                Name
                <span class="required-asterisk">*</span>
              </label>
              <MankTextField
                v-model="formData.name"
                placeholder="Enter token name"
                class="form-input"
                :error-messages="errors.name"
                hide-details="auto"/>
            </div>
            <div class="form-group">
              <label class="form-label">Description</label>
              <MankTextarea
                v-model="formData.description"
                placeholder="Optional description for this token"
                variant="outlined"
                :error-messages="errors.description"
                hide-details="auto"
                rows="4"
                class="description-textarea"/>
            </div>
            <div class="form-group">
              <label class="form-label">Expiration date</label>
              <MankTextField
                v-model="formData.expires_at"
                type="date"
                placeholder="YYYY-MM-DD (optional)"
                class="form-input"
                :error-messages="errors.expires_at"
                :min="minExpirationDate"
                hide-details="auto"/>
            </div>
          </div>
        </div>

        <div v-else class="token-success-panel">
          <div class="success-icon mb-4">
            <v-icon size="64" color="success">
              mdi-check-circle
            </v-icon>
          </div>
          <h3 class="ds-panel-title mb-2">
            Token created
          </h3>
          <p class="text-body-2 text--secondary mb-4">
            Copy your token now. It won't be shown again.
          </p>
          <div class="token-display-box mb-4">
            <code class="token-value">{{ createdToken }}</code>
            <v-btn
              class="copy-token-btn"
              variant="outlined"
              size="small"
              @click="copyCreatedToken">
              <v-icon start size="18">
                mdi-content-copy
              </v-icon>
              Copy
            </v-btn>
          </div>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            @click="goToList">
            Go to API Tokens
          </v-btn>
        </div>

        <div class="footer-buttons" v-if="!createdToken">
          <v-btn
            variant="text"
            size="medium"
            class="cancel-btn"
            elevation="0"
            @click="goBack">
            <Icon name="cross" variant="solid" size="20" color="default"/>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            size="medium"
            class="create-btn"
            elevation="0"
            :loading="loading"
            :disabled="!isFormValid || loading"
            @click="createToken">
            <Icon name="plus" variant="solid" size="20" color="onPrimary"/>
            Create
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import ApiTokenService from '@/services/ApiTokenService';
import AnalyticsService from '@/services/AnalyticsService';
import { EVENTS } from '@/constants/analyticsEvents';

const STORAGE_PREFIX = 'api_token_';

export default {
  name: 'CreateApiToken',
  components: { Icon },
  data() {
    return {
      loading: false,
      formData: {
        name: '',
        description: '',
        expires_at: '',
      },
      errors: {
        name: [],
        description: [],
        expires_at: [],
      },
      createdToken: null,
      createdTokenId: null,
    };
  },
  computed: {
    minExpirationDate() {
      const today = new Date();
      return today.toISOString().slice(0, 10);
    },
    isFormValid() {
      return this.formData.name.trim() !== '';
    },
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'ApiTokens' });
    },
    goToList() {
      this.$router.push({ name: 'ApiTokens' });
    },
    async createToken() {
      this.loading = true;
      this.errors = { name: [], description: [], expires_at: [] };
      if (this.formData.expires_at && this.formData.expires_at < this.minExpirationDate) {
        this.errors.expires_at = ['Expiration date cannot be in the past'];
        this.loading = false;
        return;
      }
      try {
        const payload = {
          name: this.formData.name.trim(),
          description: this.formData.description.trim() || undefined,
        };
        if (this.formData.expires_at) {
          payload.expires_at = this.formData.expires_at;
        }
        const response = await ApiTokenService.create(payload);
        if (response && response.success && response.data) {
          const { id, token } = response.data;
          AnalyticsService.track(EVENTS.API_TOKEN_CREATED, { has_expiration: !!this.formData.expires_at });
          if (token) {
            try {
              sessionStorage.setItem(STORAGE_PREFIX + id, token);
            } catch (_) {}
            this.createdToken = token;
            this.createdTokenId = id;
          } else {
            this.$store.dispatch('ui/showNotification', {
              type: 'success',
              title: 'Success',
              message: 'API token created.',
            });
            this.goToList();
          }
        } else {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: response?.message || 'Failed to create token.',
          });
        }
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.response?.data?.message || error.message || 'Failed to create token.',
        });
      } finally {
        this.loading = false;
      }
    },
    async copyCreatedToken() {
      if (!this.createdToken) return;
      try {
        await navigator.clipboard.writeText(this.createdToken);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Token copied to clipboard.',
        });
      } catch {
        const ta = document.createElement('textarea');
        ta.value = this.createdToken;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Token copied to clipboard.',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.main-content {
  padding: 24px;
  overflow: auto;
}

.left-panel {
  max-width: 560px;
}

.form-content .form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: $body-3-size;
  font-weight: 500;
  color: $light-foreground-1;
  margin-bottom: 8px;
  &.required .required-asterisk {
    color: $error-500;
  }
}

.token-success-panel {
  max-width: 480px;
  padding: 24px 0;
}
.token-display-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: $light-surface-1;
  border-radius: 8px;
  border: 1px solid $light-border;
  flex-wrap: wrap;
}
.token-value {
  flex: 1;
  min-width: 0;
  font-size: $text-size-13;
  word-break: break-all;
  color: $light-foreground-1;
}

.footer-buttons {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid $light-border;
}
.cancel-btn,
.create-btn {
  font-weight: 600;
}
</style>
